import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Card,
    CardContent,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { SUBSCRIPTION_PLANS, type SubscriptionPlan } from '../../config/plans';

interface PlanSelectionDialogProps {
    open: boolean;
    onClose: () => void;
    onSelectPlan: (plan: SubscriptionPlan) => void;
}

const PlanSelectionDialog: React.FC<PlanSelectionDialogProps> = ({
    open,
    onClose,
    onSelectPlan
}) => {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>Select a Subscription Plan</DialogTitle>
            <DialogContent>
                <Typography
                    variant="body2"
                    sx={{
                        mb: 3,
                        p: 2,
                        bgcolor: 'info.light',
                        color: 'info.contrastText',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'info.main'
                    }}
                >
                    ℹ️ <strong>US Customers Only:</strong> Subscription services are available exclusively for healthcare providers located in the United States. You will be required to provide a valid US billing address during checkout.
                </Typography>
                <Grid container spacing={3} sx={{ mt: 1 }}>
                    {SUBSCRIPTION_PLANS.filter((p) => !p.internal).map((plan) => (
                        <Grid size={{ xs: 12, md: 4 }} key={plan.id}>
                            <Card
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    '&:hover': { boxShadow: 6 }
                                }}
                            >
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Typography variant="h5" component="div" gutterBottom>
                                        {plan.name}
                                    </Typography>
                                    <List dense>
                                        {plan.features.map((feature, index) => (
                                            <ListItem key={index} disableGutters>
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    <CheckCircleIcon color="primary" fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText primary={feature} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </CardContent>
                                <DialogActions sx={{ p: 2, pt: 0 }}>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={() => onSelectPlan(plan)}
                                    >
                                        Select {plan.name}
                                    </Button>
                                </DialogActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
};

export default PlanSelectionDialog;
